import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/backend/AuthContext';
import { useWebSocket } from '../../contexts/backend/WebSocketContext';
import { useLanguage } from '../../contexts/languages/LanguageContext';
import { Loader2, WifiOff, RefreshCw, CheckCircle } from 'lucide-react';


const ProtectedLayout: React.FC = () => {
  const { isAuthenticated, isAuthLoading } = useAuth();
  const { status, connectNow } = useWebSocket();
  const { t } = useLanguage();

  const [hasBeenConnected, setHasBeenConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showConnectionPanel, setShowConnectionPanel] = useState(false);

  // Track connection status and update states
  useEffect(() => {
    if (status === 'open') {
      // Connection is now open
      const wasReconnecting = hasBeenConnected && showConnectionPanel;

      setHasBeenConnected(true);
      setReconnectAttempts(0);
      setShowConnectionPanel(false);

      if (wasReconnecting) {
        // Was disconnected, now reconnected - show success briefly
        setShowSuccessToast(true);
      }
    } else if (hasBeenConnected && status !== 'open') {
      // Was connected, now disconnected
      setReconnectAttempts(prev => prev + 1);
      setShowConnectionPanel(true);
      setShowSuccessToast(false);
    }
  }, [status, hasBeenConnected]);

  // Auto-hide success toast after 2 seconds
  useEffect(() => {
    if (showSuccessToast) {
      const timer = setTimeout(() => {
        setShowSuccessToast(false);
      }, 2000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [showSuccessToast]);

  // Show panel during initial loading
  useEffect(() => {
    const isInitialLoading = isAuthLoading || (status === 'connecting' && !hasBeenConnected);
    if (isInitialLoading) {
      setShowConnectionPanel(true);
    } else if (status === 'open' && !hasBeenConnected) {
      setShowConnectionPanel(false);
    }
  }, [isAuthLoading, status, hasBeenConnected]);

  useEffect(() => {
    const handleOnline = () => {
      if (status !== 'open') connectNow();
    };
    const handleOffline = () => {
      // Internet connection lost
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [status, connectNow]);

  if (!isAuthLoading && !isAuthenticated) {
    return <Navigate to="/?showLogin=true" replace />;
  }

  const isInitialLoading = isAuthLoading || (status === 'connecting' && !hasBeenConnected);
  const isConnectionLost = hasBeenConnected && status !== 'open';

  return (
    <>
      <Outlet />

      {/* Connection Lost / Loading Panel */}
      {showConnectionPanel && (
        <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="bg-slate-800/95 backdrop-blur-md rounded-xl border border-slate-700/50 shadow-2xl p-4 min-w-[280px]">
            <div className="flex items-center space-x-3">
              {isConnectionLost ? (
                <div className="flex-shrink-0">
                  <WifiOff className="w-5 h-5 text-orange-400" />
                </div>
              ) : (
                <div className="flex-shrink-0">
                  <Loader2 className="w-5 h-5 text-yellow-400 animate-spin" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">
                  {isConnectionLost
                    ? t.protectedLayout.connectionLost.title
                    : t.protectedLayout.connecting.title}
                </p>
                <p className="text-slate-400 text-xs mt-0.5">
                  {isConnectionLost
                    ? (status === 'connecting'
                        ? `${t.protectedLayout.connectionLost.reconnecting}...`
                        : t.protectedLayout.connectionLost.waiting)
                    : (isAuthLoading
                        ? t.protectedLayout.connecting.authenticating
                        : t.protectedLayout.connecting.establishing)}
                </p>
              </div>

              {isConnectionLost && (
                <button
                  onClick={connectNow}
                  className="flex-shrink-0 p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                  title="Retry connection"
                >
                  <RefreshCw className="w-4 h-4 text-slate-300" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Toast - Shown when connection is restored */}
      {showSuccessToast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="bg-green-800/95 backdrop-blur-md rounded-xl border border-green-700/50 shadow-2xl p-4 min-w-[280px]">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">
                  {t.protectedLayout?.connectionRestored?.title || 'Verbindung wiederhergestellt'}
                </p>
                <p className="text-green-200 text-xs mt-0.5">
                  {t.protectedLayout?.connectionRestored?.message || 'Verbunden mit Server'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProtectedLayout;
